import React, { useState, useEffect, useRef } from 'react';
import { collection, query, onSnapshot, where, serverTimestamp } from 'firebase/firestore';
import { AnimatePresence, motion } from 'framer-motion';
import { useAppContext } from '../AppContext';
import * as LucideIcons from 'lucide-react';
import { useTranslation } from 'react-i18next';

const GlobalParticipationTicker = () => {
    const { db, appId } = useAppContext();
    const { t } = useTranslation();
    const [messages, setMessages] = useState([]);
    const [activeMessage, setActiveMessage] = useState(null);
    const isProcessing = useRef(false);
    const componentMountedTime = useRef(Date.now());

    useEffect(() => {
        if (!appId) return;

        const entriesRef = collection(db, `artifacts/${appId}/public/data/lottery/current/entries`);
        // We only want to listen for entries created after the component has mounted
        const q = query(entriesRef, where("timestamp", ">", new Date(componentMountedTime.current)));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            snapshot.docChanges().forEach((change) => {
                if (change.type === "added") {
                    const entry = change.doc.data();
                    const randomMessages = [
                        t('globalParticipationTicker.challenger'),
                        t('globalParticipationTicker.castTickets', { amount: entry.amount }),
                        t('globalParticipationTicker.hopefulSoul'),
                        t('globalParticipationTicker.potGrows', { amount: entry.amount }),
                    ];
                    const message = randomMessages[Math.floor(Math.random() * randomMessages.length)];
                    setMessages(prev => [...prev, message]);
                }
            });
        });

        return () => unsubscribe();
    }, [db, appId, t]);

    useEffect(() => {
        if (messages.length > 0 && !isProcessing.current) {
            isProcessing.current = true;
            const nextMessage = messages[0];
            setActiveMessage(nextMessage);

            setTimeout(() => {
                setActiveMessage(null);
                setMessages(prev => prev.slice(1));
                isProcessing.current = false;
            }, 4000); // Display each message for 4 seconds
        }
    }, [messages]);

    return (
        <div className="h-8 flex items-center justify-center text-sm text-purple-300 overflow-hidden relative mb-4">
            <LucideIcons.Users size={16} className="mr-2 flex-shrink-0"/>
            <AnimatePresence>
                {activeMessage && (
                    <motion.p
                        key={activeMessage} // key ensures re-render on message change
                        className="absolute animate-ticker-item"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ ease: "easeInOut", duration: 0.5 }}
                    >
                        {activeMessage}
                    </motion.p>
                )}
            </AnimatePresence>
            {!activeMessage && <p className='text-gray-500'>{t('globalParticipationTicker.cosmosWaits')}</p>}
        </div>
    );
};

export default GlobalParticipationTicker;
